import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, cn } from '@parrot-software/design-system';
import { LoaderCircle } from '@parrot-software/design-system/icons';
import { Controller, useWatch } from 'react-hook-form-v7';
import type { UseFormReturn } from 'react-hook-form-v7';
import type { EmployeeFormData } from '@personnel/presentation/types';
import { useTranslation, Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGenerateEmployeePin } from '@personnel/presentation/hooks/useGenerateEmployeePin';
import { useValidateEmployeePin } from '@personnel/presentation/hooks/useValidateEmployeePin';
import { useEmployeeFormContext } from '@personnel/presentation/components/form/EmployeeForm/hooks';
import {
  styles,
  EMPLOYEE_FORM_IDS,
  DisabledSectionHelp,
} from '@personnel/presentation/components/form/EmployeeForm/shared';
import { FormPinField } from '@personnel/presentation/components/shared';
import {
  PERMISSIONS_THAT_REQUIRE_PIN,
  getFrontendIdsForBackendIds,
} from '@personnel/presentation/components/form/EmployeeForm/utils';

interface PinSectionProps {
  form: UseFormReturn<EmployeeFormData>;
}

/**
 * PIN section component with PIN field and auto-generation functionality
 *
 * Automatically enabled/disabled based on the _pinEnabled field in the form state,
 * which is controlled by the selected role's permissions (pos.loginAccess, payTerminal.loginAccess).
 *
 * Features:
 * - Async server-side PIN validation with debounce
 * - Auto-generation of secure PINs
 * - Real-time validation feedback
 *
 * @param {PinSectionProps} props - Component properties
 * @returns {React.ReactElement} PIN section with field and generation button
 */
export const PinSection = ({ form }: PinSectionProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const { t: tRoles } = useTranslation('roles');
  const { control, setValue, clearErrors, setError } = useEmployeeFormContext(form);
  const { employeeUuid } = useParams<{ employeeUuid: string }>();

  // Read PIN enabled state from form
  const pinEnabled = useWatch({ control, name: '_pinEnabled' });
  const disablePinSectionByPermissions = !pinEnabled;

  /**
   * Get permission names that are required to enable PIN section
   */
  const requiredPinPermissionNames = useMemo(() => {
    const frontendIds = getFrontendIdsForBackendIds(PERMISSIONS_THAT_REQUIRE_PIN);
    return frontendIds.map((frontendId) => tRoles(`permissions.${frontendId}.name`)).join(', ');
  }, [tRoles]);

  const { generatePin, isGeneratingPin } = useGenerateEmployeePin();
  const { validatePin, isValidatingPin } = useValidateEmployeePin();

  const posPinValue = useWatch({ control, name: 'posPin' });
  const isPosPinDirty = !!form.formState.dirtyFields?.posPin;

  const debounceRef = useRef<number | null>(null);
  const [serverPinValid, setServerPinValid] = useState<boolean | null>(null);

  // Use refs to avoid re-running effect when functions change reference
  const validatePinRef = useRef(validatePin);
  const clearErrorsRef = useRef(clearErrors);
  const setErrorRef = useRef(setError);

  useEffect(() => {
    validatePinRef.current = validatePin;
    clearErrorsRef.current = clearErrors;
    setErrorRef.current = setError;
  }, [validatePin, clearErrors, setError]);

  /**
   * Handles PIN generation
   * Generates a new secure PIN and sets it in the form
   */
  const handleGeneratePin = useCallback(async () => {
    const newPin = await generatePin();
    setValue('posPin', newPin, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
  }, [generatePin, setValue]);

  /**
   * Validates PIN against server
   * @param pin - PIN to validate
   * @param userUuid - Optional user UUID for edit mode validation
   */
  const handlePinValidation = useCallback(async (pin: string, userUuid?: string) => {
    try {
      const isValid = await validatePinRef.current({
        pin,
        ...(userUuid && { userUuid }),
      });
      if (isValid) {
        clearErrorsRef.current('posPin');
        setServerPinValid(true);
      } else {
        setServerPinValid(false);
        setErrorRef.current('posPin', {
          type: 'server',
          message: t('form.pinSection.pinAlreadyUsed'),
        });
      }
    } catch {
      setServerPinValid(false);
    }
  }, []);

  /**
   * Effect to handle debounced PIN validation
   * Validates PIN on server after user stops typing (400ms delay)
   */
  useEffect(() => {
    // Helper to clear timeout
    const clearDebounceTimeout = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };

    // Clear previous timeout
    clearDebounceTimeout();

    const isPinValidLength = posPinValue && posPinValue.length >= 3 && posPinValue.length <= 6;

    // Reset server validation state
    setServerPinValid(null);

    // Early return: if PIN is not valid length, clear errors and exit
    if (!isPinValidLength) {
      clearErrorsRef.current('posPin');
      return clearDebounceTimeout;
    }

    if (!isPosPinDirty) {
      clearErrorsRef.current('posPin');
      return clearDebounceTimeout;
    }

    // If PIN is valid length, start validation with debounce
    debounceRef.current = window.setTimeout(() => {
      handlePinValidation(posPinValue, employeeUuid);
    }, 400);

    // Cleanup function
    return clearDebounceTimeout;
  }, [posPinValue, employeeUuid, handlePinValidation, isPosPinDirty]);

  const isDisabled = disablePinSectionByPermissions;

  return (
    <div>
      <div className={styles.containerCards}>
        <div>
          <h2 className={cn(styles.header.cardTitle, { 'text-muted-foreground': isDisabled })}>
            {t('form.pinSection.title')}
          </h2>
          <p className={cn(styles.header.description, { 'text-muted-foreground': isDisabled })}>
            <Trans components={{ bold: <strong /> }} i18nKey="form.pinSection.description" t={t} />
          </p>
        </div>
        <div>
          <div className={styles.gridGroup}>
            <Controller
              control={control}
              name="posPin"
              render={({ field }) => (
                <FormPinField
                  containerClassName={styles.field}
                  data-testid="pin-input"
                  disabled={isDisabled || isGeneratingPin}
                  field={field}
                  id={EMPLOYEE_FORM_IDS.PIN}
                  isValidating={isValidatingPin}
                  label={t('form.pinSection.pinLabel')}
                  labelClassName={cn({ 'text-muted-foreground': isDisabled })}
                  maxLength={6}
                  onChange={(event) => {
                    const digitsOnly = event.target.value.replace(/\D/g, '');
                    field.onChange(digitsOnly);
                  }}
                  placeholder={t('form.pinSection.pinPlaceholder')}
                  required={!isDisabled}
                  serverValidationState={serverPinValid}
                  value={field.value}
                />
              )}
            />
            <Button
              className="w-fit self-end"
              data-testid="pin-generate-button"
              disabled={isDisabled || isGeneratingPin}
              onClick={handleGeneratePin}
              type="button"
              variant="secondary"
            >
              {isGeneratingPin && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {isGeneratingPin ? t('form.buttonGeneratePinLoading') : t('form.buttonGeneratePin')}
            </Button>
          </div>
          <Controller
            control={control}
            name="posPin"
            render={({ fieldState }) =>
              fieldState.error?.message ? (
                <p className="text-xs text-destructive mt-1">{fieldState.error.message}</p>
              ) : (
                <></>
              )
            }
          />
        </div>
      </div>
      {disablePinSectionByPermissions && requiredPinPermissionNames && (
        <DisabledSectionHelp
          i18nKey="form.pinSection.pinDisabledHelp"
          values={{ permissions: requiredPinPermissionNames }}
        />
      )}
    </div>
  );
};
